<script lang="ts">
  import type { ImportedCity, Plan } from '$lib/types'
  import { extractBuildingDetails, extractPlayerEra, resolveBuilding } from '$lib/catalogue'

  interface Props {
    plan: Plan
    importedCity: ImportedCity | null
    selectedId: number
    onClose: () => void
    onRemoveSelected: () => void
    onDuplicateSelected: () => void
  }

  const {
    plan,
    importedCity,
    selectedId,
    onClose,
    onRemoveSelected,
    onDuplicateSelected,
  }: Props = $props()

  /**
   * Resolved selection state. Null when the selectedId no longer maps to
   * a placed building (it can disappear under us — store-tool sweep,
   * undo, reset). The parent gates this panel on `selectedId !== null`,
   * but a stale id is still possible for one render cycle, so we
   * defensively render an empty state.
   */
  const selection = $derived.by(() => {
    const b = plan.buildings.find((x) => x.id === selectedId)
    if (!b) return null
    return { building: b, resolved: resolveBuilding(b.cityentityId, importedCity ?? undefined) }
  })
</script>

{#if selection}
  {@const isGB = selection.resolved?.type === 'greatbuilding'}
  {@const lvl = selection.building.level}
  {@const playerEra = extractPlayerEra(importedCity)}
  {@const details = selection.resolved
    ? extractBuildingDetails(selection.resolved, lvl, playerEra)
    : null}

  <aside
    class="absolute left-0 top-0 z-10 flex h-full w-80 flex-col border-r border-border-rule bg-surface-card/95 text-text-primary shadow-2xl"
  >
    <header class="flex items-start justify-between border-b border-border-rule px-4 py-3">
      <h3 class="text-xs font-semibold uppercase tracking-wide text-text-secondary">Selection</h3>
      <button
        type="button"
        class="rounded p-1 text-text-secondary hover:bg-surface-card-recessed hover:text-text-primary"
        aria-label="Clear selection"
        onclick={onClose}>✕</button
      >
    </header>

    <section class="flex-1 overflow-y-auto px-4 py-3">
      <h2 class="text-base font-semibold leading-tight">
        {selection.resolved ? selection.resolved.name : 'Unknown entity'}{#if isGB && typeof lvl === 'number'}<span
            class="font-normal text-text-secondary">{' · Lvl ' + lvl}</span
          >{/if}
      </h2>

      <div class="mt-3 space-y-2 text-xs">
        {#if details && details.production.length > 0}
          <div class="rounded bg-surface-card-recessed p-2">
            <h4 class="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
              Production
            </h4>
            <ul class="mt-1 space-y-0.5">
              {#each details.production as line, i (i)}
                <li class="flex items-baseline justify-between gap-2">
                  <span class="text-text-primary">{line.label}</span>
                  {#if line.rate}
                    <span class="text-text-muted">{line.rate}</span>
                  {/if}
                </li>
              {/each}
            </ul>
          </div>
        {/if}

        {#if details && details.features.length > 0}
          <div class="rounded bg-surface-card-recessed p-2">
            <h4 class="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
              Features
            </h4>
            <ul class="mt-1 space-y-0.5 text-text-primary">
              {#each details.features as f, i (i)}
                <li>{f.text}</li>
              {/each}
            </ul>
          </div>
        {/if}

        <div class="rounded bg-surface-card-recessed p-2">
          <h4 class="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
            Details
          </h4>
          <dl class="mt-1 space-y-0.5">
            <div class="flex justify-between">
              <dt class="text-text-secondary">Type</dt>
              <dd class="text-text-primary">{selection.resolved?.type ?? 'unknown'}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-text-secondary">Size</dt>
              <dd class="text-text-primary">
                {#if selection.resolved && selection.resolved.width && selection.resolved.height}
                  {selection.resolved.width} × {selection.resolved.height}
                {:else}
                  —
                {/if}
              </dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-text-secondary">Position</dt>
              <dd class="text-text-primary">({selection.building.x}, {selection.building.y})</dd>
            </div>
          </dl>
        </div>
      </div>
    </section>

    <section class="shrink-0 border-t-2 border-border-rule px-4 py-3">
      <div class="flex gap-2">
        <button
          type="button"
          class="flex-1 rounded bg-status-neg/30 px-2 py-1 text-xs text-status-neg hover:bg-status-neg/50"
          onclick={onRemoveSelected}>Remove</button
        >
        <button
          type="button"
          class="flex-1 rounded bg-surface-card-recessed px-2 py-1 text-xs text-text-primary hover:bg-text-primary/10"
          onclick={onDuplicateSelected}>Duplicate</button
        >
      </div>
    </section>
  </aside>
{/if}
